// One rite in play: the table now, every table before it for undo, and what
// the player has spent. The table draws it; the engine judges it.

import { RULES } from "../config/rules.ts"
import { applyMove, createState, isWon, type GameEvent, type Move, type Refusal, type TableState } from "./engine.ts"
import { ok, type Result } from "./result.ts"
import type { Rite } from "./rite.ts"

export type Step = { readonly state: TableState; readonly move: Move }

export type Game = {
  readonly rite: Rite
  readonly start: TableState
  readonly state: TableState
  readonly history: readonly Step[]
  readonly made: number // every move made, undone ones too: the honest count
  readonly aid: number // help taken this rite, weighted (src/config/rules.ts)
  readonly undos: number
  readonly rosettaPaid: boolean // the Rosetta was read during this rite
}

// A rite in progress is saved as its moves, replayed from the deal. Small,
// and it can never hold a table the laws would not allow.
export type SavedPlay = {
  readonly moves: readonly Move[]
  readonly made: number
  readonly aid: number
  readonly undos: number
  readonly rosettaPaid: boolean
}

export const newGame = (rite: Rite): Game => {
  const start = createState(rite.deal)
  return { rite, start, state: start, history: [], made: 0, aid: 0, undos: 0, rosettaPaid: false }
}

export const gameWon = (game: Game): boolean => isWon(game.state)

export type Played = { readonly game: Game; readonly events: readonly GameEvent[] }

export const play = (game: Game, move: Move): Result<Refusal, Played> => {
  const out = applyMove(game.state, move)
  if (out.tag === "error") return out
  const next: Game = {
    ...game,
    state: out.value.state,
    history: [...game.history, { state: game.state, move }],
    made: game.made + 1,
  }
  return ok({ game: next, events: out.value.events })
}

export const canUndo = (game: Game): boolean => game.history.length > 0

export const undo = (game: Game): Game => {
  const last = game.history.at(-1)
  if (last === undefined) return game
  return { ...game, state: last.state, history: game.history.slice(0, -1), undos: game.undos + 1 }
}

export const restart = (game: Game): Game => ({ ...game, state: game.start, history: [] })

export const spend = (game: Game, cost: number): Game => ({ ...game, aid: game.aid + cost })

// Reading the Rosetta costs a rite its gold once, however often it is
// switched on, and also when it is still on as the rite begins.
export const withRosetta = (game: Game): Game => (game.rosettaPaid ? game : { ...spend(game, RULES.aid.rosetta), rosettaPaid: true })

export const savedOf = (game: Game): SavedPlay => ({
  moves: game.history.map((h) => h.move),
  made: game.made,
  aid: game.aid,
  undos: game.undos,
  rosettaPaid: game.rosettaPaid,
})

// Replay a saved rite. A move the laws refuse ends the replay there, so a
// damaged save still opens on a legal table.
export const resume = (rite: Rite, saved: SavedPlay): Game => {
  let game = newGame(rite)
  for (const m of saved.moves) {
    const r = play(game, m)
    if (r.tag === "error") break
    game = r.value.game
  }
  return { ...game, made: Math.max(saved.made, game.history.length), aid: saved.aid, undos: saved.undos, rosettaPaid: saved.rosettaPaid }
}
